import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { getHiddenPanelByUri, getHiddenPanelParent, getPanelUri, panelGroups, panels } from '../panels';

const normalize = (uri) => (uri?.endsWith('/') && uri !== '/' ? uri.slice(0, -1) : uri);

function NavBreadcrumbs() {
    const location = useLocation();
    const pathname = normalize(location.pathname);

    // Identify the current panel and its group
    const findCurrent = () => {
        // Check if we're on a hidden sub-page route
        const hiddenParent = getHiddenPanelParent(pathname);
        if (hiddenParent) {
            const hiddenPanel = getHiddenPanelByUri(pathname);
            const group = panelGroups.find((g) =>
                g.standalone ? g.panel?.uri === hiddenParent.uri : g.panels?.some((p) => p.uri === hiddenParent.uri),
            );
            return {
                group,
                panel: hiddenParent,
                subName: hiddenPanel?.name || 'Details',
            };
        }

        // Exact-match against navigable panels
        const matchedPanel = panels.find((p) => normalize(p.uri) === pathname);
        if (!matchedPanel) return null;

        const group = panelGroups.find((g) =>
            g.standalone ? g.panel?.uri === matchedPanel.uri : g.panels?.some((p) => p.uri === matchedPanel.uri),
        );

        return { group, panel: matchedPanel, subName: null };
    };

    const current = findCurrent();

    // Home and no-match: render nothing
    if (!current || current.panel?.uri === '/') return null;

    const { group, panel, subName } = current;
    const isGrouped = group && !group.standalone;
    // Group and landing panel often share a name (Snapshots, Annotations) — showing both duplicates it.
    const groupLabel = isGrouped && group.groupLabel !== panel.name ? group.groupLabel : null;

    // Best navigation target for the group breadcrumb link
    const groupHref = isGrouped && group.panels?.length > 0 ? getPanelUri(group.panels[0].component) : '#';

    const parentHref = subName ? getPanelUri(panel.component) : null;

    return (
        <MuiBreadcrumbs
            aria-label='breadcrumb'
            separator='›'
            sx={{
                color: 'inherit',
                opacity: 0.7,
                '& .MuiBreadcrumbs-separator': {
                    opacity: 0.4,
                    mx: 0.75,
                    fontSize: '1.1rem',
                    fontWeight: 300,
                },
                '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
            }}
        >
            {/* Group label (e.g. "Annotations") */}
            {groupLabel && (
                <Link
                    component={RouterLink}
                    to={groupHref}
                    color='inherit'
                    underline='hover'
                    variant='body2'
                    sx={{
                        whiteSpace: 'nowrap',
                        opacity: 0.7,
                        '&:hover': { opacity: 1 },
                        transition: 'opacity 0.15s ease',
                    }}
                >
                    {groupLabel}
                </Link>
            )}

            {/* Parent panel name when on a sub-page (e.g. "Annotations" when on "Annotation Details") */}
            {subName && (
                <Link
                    component={RouterLink}
                    to={parentHref}
                    color='inherit'
                    underline='hover'
                    variant='body2'
                    sx={{
                        whiteSpace: 'nowrap',
                        opacity: 0.7,
                        '&:hover': { opacity: 1 },
                        transition: 'opacity 0.15s ease',
                    }}
                >
                    {panel.name}
                </Link>
            )}

            {/* Current page */}
            <Typography color='inherit' variant='body2' sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                {subName || panel.name}
            </Typography>
        </MuiBreadcrumbs>
    );
}

export default NavBreadcrumbs;
